/**
 * Search-click analytics via navigator.sendBeacon.
 *
 * Fired the moment a result is clicked (or Enter-selected), BEFORE navigation.
 * sendBeacon is designed for this: the POST is queued by the browser and
 * survives the page unload, so we don't have to delay the navigation.
 *
 * The payload is sent as text/plain (a CORS-safelisted content type) so the
 * cross-origin beacon to the Worker skips a preflight; the Worker JSON-parses
 * the text body. No IP / user id is sent — that stays out of the client.
 */

export interface ResultClickEvent {
  type: "result_click";
  q: string; // query as typed at click time
  clickedId: string; // e.g. sc_resort-1591
  clickedUrl: string;
  pos: number; // 1-based rank of the clicked result
  tab: string;
  mode: string; // "modal" | "results"
  total: number; // result-set size
}

const endpoint = import.meta.env.VITE_SEARCH_EVENTS_URL as string | undefined;

export function trackResultClick(ev: ResultClickEvent): void {
  const body = JSON.stringify({ ...ev, ts: Date.now() });
  if (endpoint && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      navigator.sendBeacon(endpoint, new Blob([body], { type: "text/plain" }));
      return;
    } catch {
      /* fall through to dev logging */
    }
  }
  // No endpoint configured (e.g. local dev / standalone harness): surface the
  // event so it can be verified in the console during a smoke test.
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug("[scgb-search] event", body);
  }
}
